import { useContext, useEffect, useMemo, useState } from 'react';

import AddTask from '@/components/AddTask';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import TaskItem from '@/components/TaskItem';
import defaultTaskFilters from '@/constants/defaultTaskFilters';
import taskSortOptions, { defaultTaskSortOption } from '@/constants/taskSortOptions';
import AppContext from '@/contexts/appContext';
import TaskFilterForm from '@/forms/TaskFilterForm';
import type { TaskFilters } from '@/forms/TaskFilterForm/types';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import WorkRepository from '@/repositories/WorkRepository';
import type { ItemParams } from '@/types/item';
import Container from '@/ui/components/Container';
import type { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import getInitialFilters from '@/utils/getInitialFilters';

interface Props {
  showAddTask?: boolean;
}

const Tasks = ({ showAddTask = true }: Props) => {
  const { currentUser } = useContext(AppContext);
  const [totalPages, setTotalPages] = useState<number>(0);

  const {
    params: queryParams,
    setParams: setQueryParams,
    hasQueryParams
  } = useQueryString<Partial<ItemParams>>();

  const initialSort = useMemo(() => {
    return (
      taskSortOptions.find((option) => option.value === queryParams.ordering) ??
      defaultTaskSortOption
    );
  }, [queryParams.ordering]);

  const [params, setParams] = useState<ItemParams>(() => {
    if (hasQueryParams) {
      const { ordering: _ordering, ...filters } = queryParams;
      return { ...filters, page: queryParams.page || '1' } as ItemParams;
    }

    return {
      ...getInitialFilters({
        defaultFilters: {
          ...defaultTaskFilters,
          assigned_user_id: currentUser?.staff_id ? [currentUser.staff_id] : undefined
        }
      }),
      page: '1'
    };
  });
  const [sort, setSort] = useState<OptionType<string>>(initialSort);
  const workRepo = WorkRepository();

  const setFilters = (filters: Partial<TaskFilters>) => {
    return setParams({ ...filters, page: '1' });
  };

  useEffect(() => {
    setQueryParams({ ...params, ordering: sort.value });
  }, [params, sort, setQueryParams]);

  const {
    data: itemsResponse,
    isPending: isLoadingItems,
    isSuccess
  } = useAPI({
    queryKey: ['getAllTasks', { ...params, ordering: sort.value }],
    queryFn: async () => {
      return await workRepo.getItems({
        params: {
          ...params,
          ordering: sort.value
        }
      });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      if (itemsResponse?.data?.page) {
        const page = itemsResponse.data.page.toString();
        setParams((currentParams) => ({ ...currentParams, page }));
      }

      if (itemsResponse?.data?.total_pages) {
        setTotalPages(itemsResponse.data.total_pages);
      }
    }
  }, [isSuccess, itemsResponse]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    parseInt(params.page, 10),
    totalPages
  );

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage]);

  return (
    <Container type="page">
      <div className="mb-4 bg-gray-100 pt-1">
        <PageHeader headingText="Tasks" />
        <TaskFilterForm
          activeFilters={params}
          setFilters={setFilters}
          setSort={setSort}
          sort={sort}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isLoadingItems && <LoadingSkeleton spinnerProps={{ label: 'Loading tasks' }} />}

        {!isLoadingItems && !itemsResponse?.data?.results.length ? (
          <div className="text-center p-8">
            <Heading text="No tasks found" value={3} />

            {showAddTask && (
              <div className="mt-3">
                <AddTask />
              </div>
            )}
          </div>
        ) : (
          <ul role="list">
            {itemsResponse?.data?.results.map((item) => (
              <li key={item.id}>
                <TaskItem item={item} />
              </li>
            ))}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default Tasks;
